/** Built-in category icon set. Category.icon in the DB is either a key of
 *  ICONS (the original hand-drawn set, kept so existing venues keep rendering)
 *  or a bundled id like "maki:restaurant" resolved through lib/iconLib.
 *  All icons are 24x24 stroke-based so they render crisply in the dark
 *  circular buttons of the POI panel (Situm look). */
import { useEffect, useSyncExternalStore } from "react";

import { ensureSet, getSet, parseIconId, subscribe } from "./iconLib.js";

const P = {
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
};

export const ICONS = {
  star: (
    <path
      {...P}
      d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.9l-5.3 2.7 1-5.8-4.2-4.1 5.9-.9z"
    />
  ),
  restaurant: (
    <g {...P}>
      <path d="M7 3v7M5 3v4a2 2 0 0 0 4 0V3M7 12v9" />
      <path d="M16 3c-1.7 0-3 2-3 5s1.3 4 3 4v9M16 3v18" />
    </g>
  ),
  shopping: (
    <g {...P}>
      <path d="M5 8h14l-1.2 12a1.8 1.8 0 0 1-1.8 1.6H8a1.8 1.8 0 0 1-1.8-1.6z" />
      <path d="M9 10V6.5a3 3 0 0 1 6 0V10" />
    </g>
  ),
  services: (
    <g {...P}>
      <circle cx="12" cy="7" r="3" />
      <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
    </g>
  ),
  health: (
    <g {...P}>
      <path d="M12 20.5S4 15 4 9.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 8 3.5c0 5.5-8 11-8 11z" />
      <path d="M8.5 11.5h2l1-2 1.5 4 1-2h2" />
    </g>
  ),
  leisure: (
    <g {...P}>
      <path d="M4 9a2 2 0 0 0 0 6v3h16v-3a2 2 0 0 0 0-6V6H4z" />
      <path d="M12 6v12" strokeDasharray="2.5 2.5" />
    </g>
  ),
  elevator: (
    <g {...P}>
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M9.5 9.5 12 7l2.5 2.5M9.5 14.5 12 17l2.5-2.5" />
    </g>
  ),
  wc: (
    <g {...P}>
      <circle cx="8" cy="5.5" r="1.8" />
      <path d="M8 9v6M8 15v5M6 21h4M5.5 12h5" />
      <circle cx="16.5" cy="5.5" r="1.8" />
      <path d="M16.5 9l-2 7h4zM16.5 16v5" />
    </g>
  ),
  beach: (
    <g {...P}>
      <path d="M4 20h16" />
      <path d="M12 20c0-6 1-10 3-13" />
      <path d="M15 7c-4-2-8-1-10 2 3-1 6-1 10-2zM15 7c1-3 4-4 7-3-2 0-5 1-7 3z" />
    </g>
  ),
  bus: (
    <g {...P}>
      <rect x="4" y="4" width="16" height="13" rx="2" />
      <path d="M4 12h16M7 20v-3M17 20v-3" />
      <circle cx="8" cy="14.5" r="0.4" />
      <circle cx="16" cy="14.5" r="0.4" />
    </g>
  ),
  parking: (
    <g {...P}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9.5 16.5v-9h3.2a2.8 2.8 0 0 1 0 5.6H9.5" />
    </g>
  ),
  defibrillator: (
    <g {...P}>
      <path d="M12 20.5S4 15 4 9.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 8 3.5c0 5.5-8 11-8 11z" />
      <path d="M13 8l-3 4.5h4L11 17" />
    </g>
  ),
  gym: (
    <g {...P}>
      <path d="M2.5 12h3M18.5 12h3M8.5 12h7" />
      <rect x="5.5" y="7.5" width="3" height="9" rx="1" />
      <rect x="15.5" y="7.5" width="3" height="9" rx="1" />
    </g>
  ),
  kids: (
    <g {...P}>
      <circle cx="12" cy="6" r="2.5" />
      <path d="M12 8.5v6M12 14.5l-3.5 5M12 14.5l3.5 5M7 11l5-1.5L17 11" />
    </g>
  ),
  atm: (
    <g {...P}>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <path d="M3.5 9.5h17M7 15h4" />
    </g>
  ),
  shop: (
    <g {...P}>
      <path d="M4 9l1.5-5h13L20 9M4 9v11h16V9M4 9h16M10 20v-6h4v6" />
    </g>
  ),
};

/** Subscribes to a bundled set, loading it on first use. Returns the icon
 *  record once available, or null (built-in id, still loading, unknown name). */
export function useBundledIcon(id) {
  const parsed = parseIconId(id);
  const set = parsed?.set ?? null;
  const catalog = useSyncExternalStore(
    subscribe,
    () => (set ? getSet(set) : null),
    () => null,
  );
  useEffect(() => {
    if (set) ensureSet(set);
  }, [set]);
  return parsed ? (catalog?.[parsed.name] ?? null) : null;
}

/** Renders either a built-in icon or a bundled one. Colour comes from the
 *  surrounding `color` (currentColor) in both cases. */
export function CategoryIcon({ icon, size = 24, className }) {
  const bundled = useBundledIcon(icon);

  if (parseIconId(icon)) {
    // Bundled: nothing to draw until the catalogue lands. Reserve the box so
    // the grid does not reflow when it does.
    if (!bundled) return <span style={{ display: "inline-block", width: size, height: size }} />;
    const stroked = bundled.s === "s";
    return (
      <svg
        viewBox={bundled.v}
        width={size}
        height={size}
        className={className}
        fill={stroked ? "none" : "currentColor"}
        stroke={stroked ? "currentColor" : "none"}
        strokeWidth={stroked ? 2 : undefined}
        strokeLinecap={stroked ? "round" : undefined}
        strokeLinejoin={stroked ? "round" : undefined}
        dangerouslySetInnerHTML={{ __html: bundled.b }}
      />
    );
  }

  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className}>
      {ICONS[icon] ?? ICONS.shop}
    </svg>
  );
}
